import { css } from "@emotion/css"
import React, { useCallback, useRef } from "react"

import CloseButton from "@p-ban/common/src/components/button/CloseButton/CloseButton"
import Modal from "@p-ban/common/src/components/lib/Modal"

import COLOR_CODE from "../../../constant/color"
import MODAL_ID from "../../../constant/modalId"
import StyledButton from "../../result/TotalResult/StyledButton"
import { useModal } from "../useModal"

type Props = {
  saveEstimate: () => void
}

const DeliveryZeroModal = ({ saveEstimate }: Props) => {
  const { isOpen, close } = useModal(MODAL_ID.DELIVERY_ZERO)

  const ref = useRef<HTMLTextAreaElement>(null)
  const select = useCallback(() => ref.current?.select(), [])

  return (
    <Modal
      contentLabel="デリバリーゼロ（出荷当日納品）オプションについて"
      isOpen={isOpen}
      onAfterOpen={select}
      onRequestClose={close}
    >
      <div className={styles.container}>
        <div>
          <div className={styles.content}>
            <h2 className={styles.mainTitle}>デリバリーゼロ（出荷当日納品）について</h2>

            <h3 className={styles.sectionTitle}>◆ サービス内容</h3>
            <p className={styles.description}>製造した基板を【出荷日当日】にお客様へお届けするサービスです。</p>

            <h3 className={styles.sectionTitle}>◆ 対象地域</h3>
            <ul className={styles.list}>
              <li>東京都（離島除く）、埼玉県、神奈川県</li>
              <li>山梨県、愛知県</li>
              <li>三重県、大阪府、京都府</li>
            </ul>

            <h3 className={styles.sectionTitle}>◆ お届け日／時間</h3>
            <ul className={styles.list}>
              <li>お届け予定日：出荷予定日と同日</li>
              <li>お届け予定時間：確定後にご連絡いたします</li>
            </ul>
            <p className={styles.note}>※製造完了見込み時間に合わせて、お届け予定時間をご連絡いたします。</p>

            <h3 className={styles.sectionTitle}>◆ お客様へのご案内事項</h3>
            <ol className={styles.orderedList}>
              <li>必ず連絡が取れるご担当者様の連絡先をご用意ください。</li>
              <li>お届け予定時間は、出荷日当日に電話またはメールにてご案内いたします。</li>
              <li>お届け時は、必ずお立ち会いをお願いいたします。</li>
              <li>お受け取り後は、「引取確認書」をご記入のうえ、当日の案内に従ってご対応ください。</li>
            </ol>

            <h3 className={styles.sectionTitle}>◆ 注記</h3>
            <ul className={styles.list}>
              <li>基板の仕様や製造条件によっては、本サービスをご利用いただけない場合がございます。</li>
              <li>配送業者は、弊社指定となります。</li>
              <li>配送業者の対応エリア内のみお届けが可能です。エリア外への配送は承っておりません。</li>
              <li>事故・災害・交通事情など、やむを得ない事由により配送が遅延する場合がございます。</li>
              <li>帳票類は、後日郵送で対応させていただく場合がございます。</li>
            </ul>
          </div>
          <div className={styles.buttonGroup}>
            <StyledButton
              onClick={() => {
                close()
                saveEstimate()
              }}
              backgroundColor={COLOR_CODE.DEEP_BLUE}
            >
              次へ進む
            </StyledButton>
            <StyledButton onClick={close} backgroundColor={COLOR_CODE.ORANGE}>
              戻る
            </StyledButton>
          </div>
        </div>
        <div>
          <CloseButton onClick={close}>閉じる</CloseButton>
        </div>
      </div>
    </Modal>
  )
}

export default DeliveryZeroModal

const styles = {
  container: css`
    display: flex;

    > *:first-child {
      width: 700px;
    }

    > *:nth-child(2) {
      padding-left: 20px;
    }
  `,

  content: css`
    color: ${COLOR_CODE.LIGHT_BLACK};
    padding-bottom: 24px;
    font-size: 13px;
  `,

  mainTitle: css`
    font-size: 16px;
    font-weight: bold;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid ${COLOR_CODE.TABLE_BORDER_DARK_GRAY};
  `,

  description: css`
    line-height: 1.6;
    margin: 0 0 16px 0;
  `,

  regionSection: css`
    margin-bottom: 16px;
    padding-bottom: 16px;
    border-top: 1px solid ${COLOR_CODE.TABLE_BORDER_DARK_GRAY};
    border-bottom: 1px solid ${COLOR_CODE.TABLE_BORDER_DARK_GRAY};
  `,

  label: css`
    font-weight: bold;
    margin-bottom: 8px;
  `,

  regionItems: css`
    padding-left: 16px;

    > div {
      line-height: 1.8;
    }
  `,

  textBlock: css`
    p {
      line-height: 1.6;
      margin: 0 0 10px 0;

      &:last-child {
        margin-bottom: 0;
      }
    }
  `,

  caution: css`
    color: ${COLOR_CODE.RED} !important;
    font-weight: bold;
    margin-top: 12px !important;
  `,

  buttonGroup: css`
    display: flex;
    gap: 12px;
    margin: 0 24px;

    > button {
      flex: 1;
    }
  `,
  sectionTitle: css`
    font-size: 14px;
    font-weight: bold;
    margin: 16px 0 8px 0;
    color: ${COLOR_CODE.LIGHT_BLACK};
  `,
  list: css`
    margin: 0 0 8px 0;
    padding-left: 24px;

    li {
      line-height: 1.2;
      margin-bottom: 4px;
    }
  `,
  orderedList: css`
    margin: 0 0 8px 0;
    padding-left: 24px;

    li {
      line-height: 1.2;
      margin-bottom: 6px;
    }
  `,
  note: css`
    font-size: 12px;
    color: ${COLOR_CODE.LIGHT_BLACK};
    margin: 0 0 8px 0;
    padding-left: 8px;
  `,
}
